import React, { useState, useEffect } from "react";
import { Button, Modal, Form, ProgressBar } from "react-bootstrap";
import { useInView } from "react-intersection-observer";
import TypingEffect from "./TypingEffect";
import "./STEMLandingPage.css";
import ReactGA from "react-ga4";
import Header from "./Header";
const TRACKING_ID = "G-1XD9QJLJGR";
ReactGA.initialize(TRACKING_ID);

const STEMLandingPage = ({ onComplete }) => {
  const [showFunnel, setShowFunnel] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: Student Type & Goals
    studentType: "",
    currentLevel: "",
    primaryGoal: "",
    timeframe: "",
    
    // Step 2: Subject Challenges
    strugglingSubjects: [],
    specificChallenges: [],
    learningStyle: "",
    studyHours: "",
    
    // Step 3: Current Study Methods
    currentMethods: [],
    effectiveness: "",
    biggestFrustration: "",
    
    // Step 4: Success Metrics
    targetGrade: "",
    examDate: "",
    successDefinition: "",
    
    // Step 5: Contact & Preferences
    firstName: "",
    email: "",
    phone: "",
    preferredContact: "",
    notifications: true
  });

  const steps = [
    {
      title: "Tell Us About Your Academic Journey",
      subtitle: "Help us understand where you are and where you want to go",
      fields: ["studentType", "currentLevel", "primaryGoal", "timeframe"]
    },
    {
      title: "What Subjects Challenge You Most?",
      subtitle: "Identify your pain points so we can help you overcome them",
      fields: ["strugglingSubjects", "specificChallenges", "learningStyle", "studyHours"]
    },
    {
      title: "Current Study Approach",
      subtitle: "Let's see what's working and what isn't",
      fields: ["currentMethods", "effectiveness", "biggestFrustration"]
    },
    {
      title: "Define Your Success",
      subtitle: "Set clear goals for your academic achievement",
      fields: ["targetGrade", "examDate", "successDefinition"]
    },
    {
      title: "Stay Connected",
      subtitle: "Get personalized study plans and progress updates",
      fields: ["firstName", "email", "phone", "preferredContact", "notifications"]
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const validateStep = (stepIndex) => {
    const stepFields = steps[stepIndex].fields;
    return stepFields.every(field => {
      const value = formData[field];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value && value.trim() !== "";
    });
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    ReactGA.event({
      category: "Funnel",
      action: "Complete",
      label: "STEM Landing Page Funnel"
    });
    
    if (onComplete) {
      onComplete(formData);
    }
    setShowFunnel(false);
  };

  const openFunnel = (source) => {
    ReactGA.event({
      category: "Funnel",
      action: "Start",
      label: source
    });
    setShowFunnel(true);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <Form.Group className="mb-3">
              <Form.Label>I am a:</Form.Label>
              <div className="radio-grid">
                {["Pre-med Student", "Nursing Student", "Pharmacy Student", "Biology Major", "Chemistry Major", "Physics Student", "High School Student", "Other"].map(type => (
                  <Form.Check
                    key={type}
                    type="radio"
                    name="studentType"
                    label={type}
                    checked={formData.studentType === type}
                    onChange={(e) => handleInputChange("studentType", type)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Current Academic Level:</Form.Label>
              <Form.Select value={formData.currentLevel} onChange={(e) => handleInputChange("currentLevel", e.target.value)}>
                <option value="">Select your level</option>
                <option value="High School">High School</option>
                <option value="College Freshman">College Freshman</option>
                <option value="College Sophomore">College Sophomore</option>
                <option value="College Junior">College Junior</option>
                <option value="College Senior">College Senior</option>
                <option value="Graduate Student">Graduate Student</option>
                <option value="Professional School">Professional School</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Primary Goal:</Form.Label>
              <Form.Select value={formData.primaryGoal} onChange={(e) => handleInputChange("primaryGoal", e.target.value)}>
                <option value="">Select your main goal</option>
                <option value="Pass Current Exams">Pass Current Exams</option>
                <option value="Improve GPA">Improve GPA</option>
                <option value="Prepare for MCAT">Prepare for MCAT</option>
                <option value="Prepare for NCLEX">Prepare for NCLEX</option>
                <option value="Master Difficult Concepts">Master Difficult Concepts</option>
                <option value="Build Strong Foundation">Build Strong Foundation</option>
                <option value="Career Preparation">Career Preparation</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Timeline:</Form.Label>
              <Form.Select value={formData.timeframe} onChange={(e) => handleInputChange("timeframe", e.target.value)}>
                <option value="">When do you need results?</option>
                <option value="Next 2 weeks">Next 2 weeks</option>
                <option value="Next month">Next month</option>
                <option value="This semester">This semester</option>
                <option value="Next semester">Next semester</option>
                <option value="This academic year">This academic year</option>
                <option value="Long-term (1+ years)">Long-term (1+ years)</option>
              </Form.Select>
            </Form.Group>
          </div>
        );

      case 1:
        return (
          <div className="step-content">
            <Form.Group className="mb-3">
              <Form.Label>Which subjects are you struggling with? (Select all that apply)</Form.Label>
              <div className="checkbox-grid">
                {["Chemistry", "Biology", "Physics", "Mathematics", "Pharmacology", "Anatomy & Physiology", "Biochemistry", "Organic Chemistry", "Statistics", "Calculus"].map(subject => (
                  <Form.Check
                    key={subject}
                    type="checkbox"
                    label={subject}
                    checked={formData.strugglingSubjects.includes(subject)}
                    onChange={(e) => handleArrayInputChange("strugglingSubjects", subject, e.target.checked)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Specific challenges you face:</Form.Label>
              <div className="checkbox-grid">
                {["Understanding complex concepts", "Memorizing information", "Applying knowledge to problems", "Time management", "Test anxiety", "Math calculations", "Reading comprehension", "Note-taking"].map(challenge => (
                  <Form.Check
                    key={challenge}
                    type="checkbox"
                    label={challenge}
                    checked={formData.specificChallenges.includes(challenge)}
                    onChange={(e) => handleArrayInputChange("specificChallenges", challenge, e.target.checked)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Learning Style:</Form.Label>
              <Form.Select value={formData.learningStyle} onChange={(e) => handleInputChange("learningStyle", e.target.value)}>
                <option value="">How do you learn best?</option>
                <option value="Visual">Visual (diagrams, charts, videos)</option>
                <option value="Auditory">Auditory (lectures, discussions)</option>
                <option value="Kinesthetic">Kinesthetic (hands-on, interactive)</option>
                <option value="Reading/Writing">Reading/Writing (notes, texts)</option>
                <option value="Mixed">Mixed approach</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Study Hours per Week:</Form.Label>
              <Form.Select value={formData.studyHours} onChange={(e) => handleInputChange("studyHours", e.target.value)}>
                <option value="">Select study time</option>
                <option value="Less than 5 hours">Less than 5 hours</option>
                <option value="5-10 hours">5-10 hours</option>
                <option value="10-20 hours">10-20 hours</option>
                <option value="20-30 hours">20-30 hours</option>
                <option value="More than 30 hours">More than 30 hours</option>
              </Form.Select>
            </Form.Group>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <Form.Group className="mb-3">
              <Form.Label>Current study methods you use:</Form.Label>
              <div className="checkbox-grid">
                {["Textbook reading", "Flashcards", "Practice problems", "Study groups", "Online videos", "Tutoring", "Office hours", "Past exams", "Note-taking apps", "Mind mapping"].map(method => (
                  <Form.Check
                    key={method}
                    type="checkbox"
                    label={method}
                    checked={formData.currentMethods.includes(method)}
                    onChange={(e) => handleArrayInputChange("currentMethods", method, e.target.checked)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>How effective are your current study methods?</Form.Label>
              <Form.Select value={formData.effectiveness} onChange={(e) => handleInputChange("effectiveness", e.target.value)}>
                <option value="">Rate effectiveness</option>
                <option value="Very effective">Very effective</option>
                <option value="Somewhat effective">Somewhat effective</option>
                <option value="Not very effective">Not very effective</option>
                <option value="Not effective at all">Not effective at all</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>What's your biggest frustration with studying?</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.biggestFrustration}
                onChange={(e) => handleInputChange("biggestFrustration", e.target.value)}
                placeholder="Tell us what makes studying difficult for you..."
              />
            </Form.Group>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <Form.Group className="mb-3">
              <Form.Label>Target Grade/Score:</Form.Label>
              <Form.Select value={formData.targetGrade} onChange={(e) => handleInputChange("targetGrade", e.target.value)}>
                <option value="">What grade are you aiming for?</option>
                <option value="A (90-100%)">A (90-100%)</option>
                <option value="B (80-89%)">B (80-89%)</option>
                <option value="C (70-79%)">C (70-79%)</option>
                <option value="Pass (varies)">Pass (varies)</option>
                <option value="Specific test score">Specific test score</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Next Important Exam/Deadline:</Form.Label>
              <Form.Control
                type="date"
                value={formData.examDate}
                onChange={(e) => handleInputChange("examDate", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Success means to me:</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.successDefinition}
                onChange={(e) => handleInputChange("successDefinition", e.target.value)}
                placeholder="Describe what academic success looks like for you..."
              />
            </Form.Group>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <Form.Group className="mb-3">
              <Form.Label>First Name:</Form.Label>
              <Form.Control
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                placeholder="Enter your first name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Address:</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter your email"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number (Optional):</Form.Label>
              <Form.Control
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="Enter your phone number"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Preferred Contact Method:</Form.Label>
              <Form.Select value={formData.preferredContact} onChange={(e) => handleInputChange("preferredContact", e.target.value)}>
                <option value="">How should we contact you?</option>
                <option value="Email">Email</option>
                <option value="Phone">Phone</option>
                <option value="Text">Text Message</option>
                <option value="App notifications">App notifications</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="I want to receive study tips and progress updates"
                checked={formData.notifications}
                onChange={(e) => handleInputChange("notifications", e.target.checked)}
              />
            </Form.Group>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="stem-landing-page">
      {/* Header 
      <header className="landing-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <h2>🧬 STEM Success</h2>
            </div>
            <nav className="header-nav">
              <a href="#features">Features</a>
              <a href="#success-stories">Success Stories</a>
              <a href="#pricing">Pricing</a>
            </nav>
            <Button 
              variant="primary" 
              className="header-cta"
              onClick={() => openFunnel("Header")}
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </header>*/}
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                From Struggling to <span className="gradient-text">Straight A's</span> in STEM
              </h1>
              <p className="hero-subtitle">
                Stop memorizing. Start understanding. Join 15,000+ students who went from failing to thriving 
                with our breakthrough visual learning system. See complex concepts come alive through 
                interactive simulations that make everything click.
              </p>
              <div className="hero-stats">
                <div className="stat">
                  <span className="stat-number">97%</span>
                  <span className="stat-label">Grade Improvement</span>
                </div>
                <div className="stat">
                  <span className="stat-number">15k+</span>
                  <span className="stat-label">Success Stories</span>
                </div>
                <div className="stat">
                  <span className="stat-number">4.9★</span>
                  <span className="stat-label">Student Rating</span>
                </div>
              </div>
              <div className="hero-cta">
                <Button 
                  size="lg" 
                  className="primary-cta"
                  onClick={() => openFunnel("Hero Primary")}
                >
                  Transform My Grades in 7 Days
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  className="secondary-cta"
                  onClick={() => openFunnel("Hero Secondary")}
                >
                  Watch Success Stories
                </Button>
              </div>
              <p className="hero-guarantee">
                ✅ Free 7-day transformation • ✅ No credit card required • ✅ See results in 48 hours
              </p>
            </div>
            <div className="hero-visual">
              <video
                src="https://andrewslearningimage-bucket.s3.amazonaws.com/media/Gifs/Homepageproteingif.mov"
                className="hero-video"
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="social-proof">
        <div className="container">
          <p className="social-proof-text">Trusted by students at America's top universities</p>
          <div className="university-logos">
            <span>Harvard Medical</span>
            <span>MIT</span>
            <span>Stanford</span>
            <span>Johns Hopkins</span>
            <span>UCLA</span>
            <span>UC Berkeley</span>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <h2 className="section-title">Tired of Studying Harder But Still Getting Nowhere?</h2>
          <p className="problem-intro">You're not alone. 73% of STEM students feel overwhelmed and lost. Here's what's really holding you back:</p>
          <div className="problem-grid">
            <div className="problem-item">
              <div className="problem-icon">😰</div>
              <h3>The Memorization Trap</h3>
              <p>You cram formulas and facts but can't apply them when the test changes the numbers or context</p>
            </div>
            <div className="problem-item">
              <div className="problem-icon">🤯</div>
              <h3>Abstract Concepts Feel Impossible</h3>
              <p>Molecular structures, physics forces, and chemical reactions seem like foreign languages</p>
            </div>
            <div className="problem-item">
              <div className="problem-icon">⏰</div>
              <h3>Time Wasted on Wrong Methods</h3>
              <p>Hours of highlighting textbooks and re-reading notes that don't stick in your brain</p>
            </div>
            <div className="problem-item">
              <div className="problem-icon">📉</div>
              <h3>Confidence Crushing Grades</h3>
              <p>Each failed test makes you question if you're "smart enough" for your dream career</p>
            </div>
          </div>
          <div className="problem-cta">
            <Button 
              size="lg" 
              className="solution-cta"
              onClick={() => openFunnel("Problem Section")}
            >
              I'm Ready for a Better Way
            </Button>
          </div>
        </div>
      </section>

      {/* Solution Features */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">The Learning Revolution That Changes Everything</h2>
          <p className="features-intro">Forget everything you know about studying. This is how top students actually master STEM:</p>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-visual">
                <video
                  src="https://andrewslearningimage-bucket.s3.amazonaws.com/media/Gifs/momentumgif.mov"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
              <div className="feature-content">
                <h3>See It. Touch It. Master It.</h3>
                <p>Watch molecules dance, manipulate 3D protein structures, and control physics simulations with your hands. When you can see and interact with concepts, your brain creates lasting neural pathways that textbooks can't.</p>
                <ul>
                  <li>3D molecular manipulation you can control</li>
                  <li>Real-time physics simulations</li>
                  <li>Interactive chemical reactions</li>
                  <li>Virtual lab experiments</li>
                </ul>
              </div>
            </div>

            <div className="feature-item reverse">
              <div className="feature-content">
                <h3>Learn Like a Medical Professional</h3>
                <p>We teach you the same "big picture first" approach used by doctors and researchers. Start with why something matters, then dive into how it works. This is the secret to understanding, not just memorizing.</p>
                <ul>
                  <li>Real-world medical case studies</li>
                  <li>Clinical application examples</li>
                  <li>Professional thinking patterns</li>
                  <li>Career-relevant contexts</li>
                </ul>
              </div>
              <div className="feature-visual">
                <video
                  src="https://andrewslearningimage-bucket.s3.amazonaws.com/media/Gifs/algebrafunctiongif.mov"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-visual">
                <div className="feature-placeholder">
                  <h4>🧠 Smart Learning AI</h4>
                </div>
              </div>
              <div className="feature-content">
                <h3>Your Personal Study Genius</h3>
                <p>Our AI watches how you learn, identifies your weak spots before you do, and creates a personalized path that adapts in real-time. It's like having a brilliant tutor who never sleeps.</p>
                <ul>
                  <li>Instant weakness detection</li>
                  <li>Adaptive difficulty progression</li>
                  <li>Personalized practice problems</li>
                  <li>Smart review scheduling</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section id="success-stories" className="testimonials-section">
        <div className="container">
          <h2 className="section-title">Real Students, Life-Changing Results</h2>
          <p className="testimonials-intro">These aren't just grade improvements. These are dreams saved and futures secured.</p>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"I was failing organic chemistry and about to give up on pre-med. The interactive molecular models finally made everything click. I went from a 52% to 94% in 6 weeks. I'm now in medical school!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Sarah Chen</h4>
                  <span>Pre-med Student → Medical School</span>
                </div>
                <div className="grade-improvement">
                  <span className="before">F</span>
                  <span className="arrow">→</span>
                  <span className="after">A</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"Physics was my nightmare. I couldn't visualize forces or understand momentum. This platform's simulations let me actually 'play' with physics concepts. I aced my final and changed my major to engineering!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Marcus Rodriguez</h4>
                  <span>Struggling Student → Engineering Major</span>
                </div>
                <div className="grade-improvement">
                  <span className="before">D</span>
                  <span className="arrow">→</span>
                  <span className="after">A-</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"My nursing pharmacology class was impossible until I found this. The drug interaction simulations and visual pathways made complex mechanisms simple. I passed NCLEX on my first try!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Jessica Thompson</h4>
                  <span>Nursing Student → Registered Nurse</span>
                </div>
                <div className="grade-improvement">
                  <span className="before">C-</span>
                  <span className="arrow">→</span>
                  <span className="after">A</span>
                </div>
              </div>
            </div>
          </div>
          <div className="testimonials-cta">
            <Button 
              size="lg" 
              className="success-cta"
              onClick={() => openFunnel("Success Stories")}
            >
              I Want Results Like These
            </Button>
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="urgency-section">
        <div className="container">
          <h2 className="urgency-title">Your Next Exam Could Change Everything</h2>
          <p className="urgency-text">
            Every day you wait is another day of ineffective studying. While you're struggling with outdated methods, 
            thousands of students are already transforming their grades and securing their futures.
          </p>
          <div className="urgency-stats">
            <div className="urgency-stat">
              <span className="stat-number">48 hrs</span>
              <span className="stat-text">Average time to see first breakthrough</span>
            </div>
            <div className="urgency-stat">
              <span className="stat-number">3.2x</span>
              <span className="stat-text">Faster mastery vs traditional studying</span>
            </div>
          </div>
          <Button 
            size="lg" 
            className="urgency-cta"
            onClick={() => openFunnel("Urgency")}
          >
            Start My Transformation Now
          </Button>
          <p className="urgency-subtext">Join 847 students who started this week</p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <h2 className="section-title">Choose Your Success Path</h2>
          <p className="pricing-intro">Less than what you'd spend on a single tutoring session. More powerful than any textbook.</p>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Free Discovery</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">0</span>
                <span className="period">/7 days</span>
              </div>
              <ul className="features-list">
                <li>✅ Full platform access</li>
                <li>✅ All interactive simulations</li>
                <li>✅ Basic progress tracking</li>
                <li>✅ Community support</li>
                <li>✅ Mobile app included</li>
              </ul>
              <Button 
                className="pricing-cta"
                onClick={() => openFunnel("Free Trial")}
              >
                Start Free Discovery
              </Button>
              <p className="pricing-note">No credit card required</p>
            </div>

            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <h3>Grade Transformer</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">39</span>
                <span className="period">/month</span>
              </div>
              <ul className="features-list">
                <li>✅ Everything in Free Discovery</li>
                <li>✅ AI-powered study plans</li>
                <li>✅ Advanced analytics & insights</li>
                <li>✅ Priority support</li>
                <li>✅ Exam preparation tools</li>
                <li>✅ Career guidance resources</li>
              </ul>
              <Button 
                className="pricing-cta primary"
                onClick={() => openFunnel("Student Plan")}
              >
                Transform My Grades
              </Button>
              <p className="pricing-note">Most students see results in 48 hours</p>
            </div>

            <div className="pricing-card">
              <h3>Future Professional</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">69</span>
                <span className="period">/month</span>
              </div>
              <ul className="features-list">
                <li>✅ Everything in Grade Transformer</li>
                <li>✅ 1-on-1 expert mentoring</li>
                <li>✅ Custom content creation</li>
                <li>✅ Career pathway guidance</li>
                <li>✅ Medical school prep tools</li>
                <li>✅ Professional network access</li>
              </ul>
              <Button 
                className="pricing-cta"
                onClick={() => openFunnel("Premium")}
              >
                Secure My Future
              </Button>
              <p className="pricing-note">For serious pre-med & pre-health students</p>
            </div>
          </div>
          <p className="pricing-guarantee">
            💰 30-day money-back guarantee • 🔒 Cancel anytime • 📞 24/7 student support
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <h2 className="final-cta-title">Your Dream Career is Waiting. Are You?</h2>
          <p className="final-cta-text">
            Every successful doctor, engineer, and scientist started exactly where you are now. The only difference? 
            They found the right way to learn. This is your moment to join them.
          </p>
          <Button 
            size="lg" 
            className="final-cta-button"
            onClick={() => openFunnel("Final CTA")}
          >
            Yes, I'm Ready to Succeed
          </Button>
          <p className="final-cta-subtext">
            Free 7-day trial • No credit card required • Join 15,000+ successful students
          </p>
          <div className="final-urgency">
            <p>⚡ 847 students joined this week • 🔥 Limited spots available</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>🧬 STEM Success</h4>
              <p>Transforming STEM education through interactive learning.</p>
              <p className="copyright">© 2024 STEM Success. All rights reserved.</p>
            </div>
            <div className="footer-section">
              <h5>Product</h5>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#success-stories">Success Stories</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h5>Support</h5>
              <ul>
                <li><a href="/help">Help Center</a></li>
                <li><a href="/contact">Contact Us</a></li>
                <li><a href="/community">Community</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h5>Company</h5>
              <ul>
                <li><a href="/about">About</a></li>
                <li><a href="/careers">Careers</a></li>
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/terms">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 STEM Success Platform. All rights reserved. | Empowering students worldwide through innovative STEM education.</p>
            <div className="footer-legal">
              <a href="/privacy">Privacy Policy</a>
              <span>•</span>
              <a href="/terms">Terms of Service</a>
              <span>•</span>
              <a href="/cookies">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Funnel Modal */}
      <Modal 
        show={showFunnel} 
        onHide={() => setShowFunnel(false)} 
        size="lg" 
        centered
        className="funnel-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {currentStep < steps.length ? steps[currentStep].title : "Thank You!"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentStep < steps.length && (
            <>
              <ProgressBar 
                now={(currentStep + 1) / steps.length * 100} 
                className="mb-4"
                label={`Step ${currentStep + 1} of ${steps.length}`}
              />
              <p className="step-subtitle">{steps[currentStep].subtitle}</p>
              {renderStepContent()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="modal-navigation">
            {currentStep > 0 && (
              <Button variant="outline-secondary" onClick={prevStep}>
                Previous
              </Button>
            )}
            <Button 
              variant="primary" 
              onClick={nextStep}
              disabled={!validateStep(currentStep)}
            >
              {currentStep === steps.length - 1 ? "Complete Setup" : "Next Step"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default STEMLandingPage; 
 
 